import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
} from '@mui/material';
import { css, styled as muiStyled, useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import MicrosoftLoginButton from './microsoft-login-button';
import OktaLoginButton from './okta-login-button';
import SmallForm from './small-form';
import Arranger from '../base/arranger';
import LogoSelector from '../base/icon/logo-selector';
import Spacer, { spc } from '../base/spacer';
import Text from '../base/text';

import { useMicrosoftAuth } from '~/client/hooks/use-microsoft-auth';
import { useOktaAuth } from '~/client/hooks/use-okta-auth';
import { nodeAuthApi } from '~/client/services/node-auth';
import * as GQL from '~/graphql/generated/client';

interface Props {
  className?: string;
}

const AcceptInvitationImpl: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const prefersDarkMode = theme.palette.mode === 'dark';
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const token = searchParams.get('token');

  // Fetch current user directly (don't skip on public routes)
  const {
    data: currentUser,
    isLoading: currentUserLoading,
    error: currentUserError,
  } = nodeAuthApi.useCurrentUserQuery();

  const {
    data: invitationData,
    isLoading: invitationLoading,
    error: invitationError,
  } = nodeAuthApi.useInvitationVerificationQuery(
    { token: token! },
    { skip: !token }
  );

  const invitation = invitationData?.invitation;

  // User is considered authenticated if currentUser exists and is email verified
  const isUserAuthenticated =
    currentUser?.email_verified && currentUser?.is_active;

  // Check if the invited email has an existing account
  const { data: emailCheckData, isLoading: emailCheckLoading } =
    nodeAuthApi.useCheckEmailExistsQuery(invitation?.email ?? '', {
      skip: !invitation?.email || isUserAuthenticated,
    });

  const { isEnabled: isMicrosoftEnabled } = useMicrosoftAuth();
  const { isEnabled: isOktaEnabled } = useOktaAuth();
  const showMicrosoftButton =
    !!invitation?.organization?.hasSso && isMicrosoftEnabled;
  const showOktaButton = !!invitation?.organization?.hasSso && isOktaEnabled;

  const [logout] = nodeAuthApi.useLogoutMutation();

  const [acceptInvitation, { loading: acceptLoading }] =
    GQL.useAcceptInvitationMutation({
      refetchQueries: [
        GQL.OrganizationInvitationsPaginatedDocument,
        GQL.OrganizationMembersPaginatedDocument,
      ],
    });

  useEffect(() => {
    if (invitationError) {
      setError('Invalid or expired invitation');
    }
  }, [invitationError]);

  // Handle email mismatch - logout and redirect back
  useEffect(() => {
    const handleEmailMismatch = async () => {
      if (
        isUserAuthenticated &&
        invitation?.email &&
        currentUser?.user_email !== invitation.email &&
        !isLoggingOut
      ) {
        setIsLoggingOut(true);
        try {
          await logout();
          // After logout, the page will reload via logout mutation
          // and user will see the proper sign-in/sign-up flow
        } catch (err) {
          setIsLoggingOut(false);
          console.error('Logout error:', err);
        }
      }
    };

    handleEmailMismatch();
  }, [isUserAuthenticated, currentUser, invitation, isLoggingOut, logout]);

  const handleAcceptInvitation = async () => {
    if (!token) {
      setError('No invitation token provided');
      return;
    }

    if (!isUserAuthenticated) {
      // Redirect to sign-in with invitation token
      navigate(`/sign-in?invitation=${token}`);
      return;
    }

    try {
      const result = await acceptInvitation({
        variables: {
          input: { token },
        },
      });

      if (result.data?.acceptInvitation.success) {
        setSuccess(
          result.data.acceptInvitation.message ??
            'Invitation accepted successfully'
        );
        setTimeout(() => {
          navigate('/dashboard/compliance'); // route guard redirects MEMBER/AUDITOR to /dashboard/tasks
        }, 3000);
      } else {
        setError('Failed to accept invitation');
      }
    } catch (err: any) {
      setError(
        err.graphQLErrors?.[0]?.message ||
          err.message ||
          'Failed to accept invitation'
      );
    }
  };

  const handleSignIn = () => {
    navigate(`/sign-in?invitation=${token}`);
  };

  const handleSignUp = () => {
    navigate(
      `/sign-up?invitation=${token}&email=${encodeURIComponent(invitation?.email ?? '')}`
    );
  };

  if (isLoggingOut) {
    return (
      <SmallForm className={className}>
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
        >
          <Arranger
            direction="column"
            alignItems="center"
            sx={{ pb: spc.between_vertical_m }}
          >
            <div className="logo-container">
              <LogoSelector prefersDarkMode={prefersDarkMode} />
            </div>
          </Arranger>

          <Text sz="title" align="center">
            Switching Account...
          </Text>
          <Spacer pb={spc.m} />
          <Text sz="body" align="center" color="text.secondary">
            Logging you out to continue with the invited email address.
          </Text>
          <Spacer pb={spc.m} />
          <Box display="flex" justifyContent="center">
            <CircularProgress size={30} />
          </Box>
        </Arranger>
      </SmallForm>
    );
  }

  if (invitationLoading || emailCheckLoading || currentUserLoading) {
    return (
      <SmallForm className={className}>
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
        >
          {/* Logo skeleton */}
          <Arranger
            direction="column"
            alignItems="center"
            sx={{ pb: spc.between_vertical_m }}
          >
            <Skeleton variant="rectangular" width={120} height={36} />
          </Arranger>

          {/* Message skeleton */}
          <Skeleton variant="text" width="80%" height={32} />
          <Spacer pb={spc.xs} />
          <Skeleton variant="text" width="60%" height={24} />

          <Spacer pb={spc.m} />

          {/* Button skeleton */}
          <Skeleton variant="rounded" width={140} height={48} />
        </Arranger>
      </SmallForm>
    );
  }

  if (error) {
    return (
      <SmallForm className={className}>
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
        >
          <Arranger
            direction="column"
            alignItems="center"
            sx={{ pb: spc.between_vertical_m }}
          >
            <div className="logo-container">
              <LogoSelector prefersDarkMode={prefersDarkMode} />
            </div>
          </Arranger>

          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Text sz="body" align="center" color="text.secondary">
            This invitation link may be expired, invalid, or has already been
            used.
          </Text>
          <Spacer pb={spc.m} />
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate('/sign-in')}
              sx={{ color: 'white' }}
            >
              Go to Sign In
            </Button>
            <Button variant="outlined" onClick={() => navigate('/sign-up')}>
              Create Account
            </Button>
          </Box>
        </Arranger>
      </SmallForm>
    );
  }

  if (success) {
    return (
      <SmallForm className={className}>
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
        >
          <Arranger
            direction="column"
            alignItems="center"
            sx={{ pb: spc.between_vertical_m }}
          >
            <div className="logo-container">
              <LogoSelector prefersDarkMode={prefersDarkMode} />
            </div>
          </Arranger>
          <Text sz="title" align="center">
            🎉 Welcome to Asenion!
          </Text>
          <Spacer pb={spc.m} />
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
          <Text sz="body" align="center" color="text.secondary">
            Redirecting you to the dashboard...
          </Text>
          <Spacer pb={spc.m} />
          <Box display="flex" justifyContent="center">
            <CircularProgress size={30} />
          </Box>
        </Arranger>
      </SmallForm>
    );
  }

  const userExists = emailCheckData?.exists ?? false;

  if (!invitation) {
    return (
      <SmallForm className={className}>
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
        >
          <Arranger
            direction="column"
            alignItems="center"
            sx={{ pb: spc.between_vertical_m }}
          >
            <img
              src="/images/new-logo-fairly-light.png"
              height={36}
              alt="Fairly Logo"
            />
          </Arranger>
          <Text sz="title" align="center">
            ❌ Invitation Not Found
          </Text>
          <Spacer pb={spc.m} />
          <Text sz="body" align="center" color="text.secondary">
            We couldn't find an invitation with this token.
          </Text>
          <Spacer pb={spc.m} />
          <Button variant="contained" onClick={() => navigate('/sign-in')}>
            Go to Sign In
          </Button>
        </Arranger>
      </SmallForm>
    );
  }

  const isExpired = new Date(invitation.expiresAt) < new Date();

  if (isExpired) {
    return (
      <SmallForm className={className}>
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
        >
          <Arranger
            direction="column"
            alignItems="center"
            sx={{ pb: spc.between_vertical_m }}
          >
            <img
              src="/images/new-logo-fairly-light.png"
              height={36}
              alt="Fairly Logo"
            />
          </Arranger>
          <Text sz="title" align="center">
            ⏰ Invitation Expired
          </Text>
          <Spacer pb={spc.m} />
          <Alert severity="warning" sx={{ mb: 3 }}>
            This invitation has expired.
          </Alert>
          <Text sz="body" align="center" color="text.secondary">
            Please contact {invitation.inviter.firstName}{' '}
            {invitation.inviter.lastName} or your organization administrator for
            a new invitation.
          </Text>
          <Spacer pb={spc.m} />
          <Button variant="contained" onClick={() => navigate('/sign-in')}>
            Go to Sign In
          </Button>
        </Arranger>
      </SmallForm>
    );
  }

  return (
    <SmallForm className={className}>
      <Arranger
        direction="column"
        alignItems="center"
        sx={{ pt: spc.between_vertical_l, pb: spc.between_vertical_l }}
      >
        <Arranger
          direction="column"
          alignItems="center"
          sx={{ pb: spc.between_vertical_m }}
        >
          <div className="logo-container">
            <LogoSelector prefersDarkMode={prefersDarkMode} />
          </div>
        </Arranger>

        <Text sz="title" align="center">
          🎉 You're Invited!
        </Text>
        <Text sz="body" align="center" color="text.secondary">
          Join your team on Asenion
        </Text>
        <Spacer pb={spc.m} />

        {!isUserAuthenticated ? (
          <Box width="100%">
            {userExists ? (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Text sz="body">
                    Our record indicates that you already have an account with
                    us. Please sign in to accept the invitation.
                  </Text>
                </Alert>
                <Spacer pb={spc.xs} />
                <Text sz="body" align="center" color="text.secondary">
                  This invitation was sent to{' '}
                  <strong>{invitation.email}</strong>
                </Text>
                <Spacer pb={spc.m} />
                <Box display="flex" justifyContent="center">
                  <Button
                    sx={{ color: 'white' }}
                    variant="contained"
                    onClick={handleSignIn}
                    disabled={acceptLoading}
                    fullWidth
                  >
                    Sign In
                  </Button>
                </Box>
                {showMicrosoftButton && (
                  <>
                    <Divider sx={{ my: 2 }}>
                      <Text sz="body12" color="text.secondary">
                        OR
                      </Text>
                    </Divider>
                    <MicrosoftLoginButton
                      invitationToken={token!}
                      onError={(errorMessage) => setError(errorMessage)}
                      on2FARequired={() =>
                        setError(
                          'Two-factor authentication required — please sign in normally to continue.'
                        )
                      }
                    />
                  </>
                )}
                {showOktaButton && (
                  <>
                    {!showMicrosoftButton && (
                      <Divider sx={{ my: 2 }}>
                        <Text sz="body12" color="text.secondary">
                          OR
                        </Text>
                      </Divider>
                    )}
                    <OktaLoginButton
                      invitationToken={token!}
                      onError={(errorMessage) => setError(errorMessage)}
                      on2FARequired={() =>
                        setError(
                          'Two-factor authentication required — please sign in normally to continue.'
                        )
                      }
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Text sz="body">
                    Create an account to accept the invitation and join{' '}
                    {invitation.organization.name}.
                  </Text>
                </Alert>
                <Spacer pb={spc.xs} />
                <Text sz="body" align="center" color="text.secondary">
                  This invitation was sent to{' '}
                  <strong>{invitation.email}</strong>
                </Text>
                <Spacer pb={spc.m} />
                <Box display="flex" justifyContent="center">
                  <Button
                    sx={{ color: 'white' }}
                    variant="contained"
                    onClick={handleSignUp}
                    disabled={acceptLoading}
                    fullWidth
                  >
                    Create Account
                  </Button>
                </Box>
                {showMicrosoftButton && (
                  <>
                    <Divider sx={{ my: 2 }}>
                      <Text sz="body12" color="text.secondary">
                        OR
                      </Text>
                    </Divider>
                    <MicrosoftLoginButton
                      invitationToken={token!}
                      onError={(errorMessage) => setError(errorMessage)}
                      on2FARequired={() =>
                        setError(
                          'Two-factor authentication required — please sign in normally to continue.'
                        )
                      }
                    />
                  </>
                )}
                {showOktaButton && (
                  <>
                    {!showMicrosoftButton && (
                      <Divider sx={{ my: 2 }}>
                        <Text sz="body12" color="text.secondary">
                          OR
                        </Text>
                      </Divider>
                    )}
                    <OktaLoginButton
                      invitationToken={token!}
                      onError={(errorMessage) => setError(errorMessage)}
                      on2FARequired={() =>
                        setError(
                          'Two-factor authentication required — please sign in normally to continue.'
                        )
                      }
                    />
                  </>
                )}
              </>
            )}
          </Box>
        ) : (
          <Box width="100%">
            <Box>
              <Text sz="body" align="center" color="text.secondary">
                Ready to join {invitation.organization.name}? Click the button
                below to accept your invitation.
              </Text>
              <Spacer pb={spc.m} />

              <Box display="flex" justifyContent="center">
                <Button
                  sx={{ color: 'white' }}
                  variant="contained"
                  onClick={handleAcceptInvitation}
                  disabled={acceptLoading}
                  startIcon={
                    acceptLoading ? <CircularProgress size={20} /> : null
                  }
                >
                  {acceptLoading ? 'Accepting...' : 'Accept Invitation'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        <Spacer pb={spc.m} />
        <Text
          sz="body"
          align="center"
          color="text.secondary"
          fontSize="0.75rem"
        >
          This invitation expires on{' '}
          {new Date(invitation.expiresAt).toLocaleDateString()}
        </Text>
      </Arranger>
    </SmallForm>
  );
};

export const AcceptInvitation = muiStyled(AcceptInvitationImpl)(
  ({ theme }) => css`
    .logo-container {
      svg {
        width: 150px;
        height: 50px;
      }
    }
  `
);

export default AcceptInvitation;
